
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

const ConditionsMegaMenu = () => {
  const [isOpen, setIsOpen] = useState(false);

  const conditions = [
    {
      icon: "🔴",
      concern: "Acne & Scars",
      description: "Clear skin and smooth texture",
      treatments: ["RF Microneedling", "Laser", "Peels"],
      link: "/conditions/acne-scars"
    },
    {
      icon: "📏",
      concern: "Fine Lines & Wrinkles",
      description: "Smooth lines and prevent aging",
      treatments: ["Botox", "Fillers", "Laser"],
      link: "/conditions/fine-lines-wrinkles"
    },
    {
      icon: "🟤",
      concern: "Pigmentation & Spots",
      description: "Even skin tone and reduce spots",
      treatments: ["Laser", "Peels", "IPL"],
      link: "/conditions/pigmentation-spots"
    },
    {
      icon: "💧",
      concern: "Dull & Dehydrated Skin",
      description: "Restore radiance and hydration",
      treatments: ["HydraFacial", "Skin Boosters", "LED"],
      link: "/conditions/dull-dehydrated-skin"
    },
    {
      icon: "🌊",
      concern: "Uneven Tone & Texture",
      description: "Smooth and refine skin surface",
      treatments: ["Microneedling", "Peels", "Laser"],
      link: "/conditions/uneven-tone-texture"
    },
    {
      icon: "🎯",
      concern: "Body Fat & Double Chin",
      description: "Sculpt and contour your body",
      treatments: ["Kybella", "Body Sculpting"],
      link: "/conditions/body-fat-double-chin"
    }
  ];

  return (
    <div 
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="flex items-center space-x-1 text-juv-deep-chestnut hover:text-juv-terracotta transition-colors font-light tracking-wide py-2">
        <span>Conditions</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute top-full left-0 mt-2 w-[800px] -translate-x-1/4 z-50"
          >
            <div className="bg-white/95 backdrop-blur-md border border-juv-rose-clay/20 rounded-2xl shadow-luxury overflow-hidden">
              <div className="bg-gradient-to-r from-juv-soft-blush/10 to-juv-rose-clay/10 border-b border-juv-rose-clay/20 p-6">
                <h3 className="text-xl font-bold text-juv-deep-chestnut">Conditions We Treat</h3>
                <p className="text-juv-deep-chestnut/80 text-sm mt-1">Find the perfect treatment for your concerns</p>
              </div>

              <div className="grid grid-cols-2 gap-4 p-6">
                {conditions.map((condition, index) => (
                  <motion.div
                    key={condition.concern}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                    className="group"
                  >
                    <Link
                      to={condition.link}
                      className="flex items-start space-x-4 p-4 rounded-xl hover:bg-juv-soft-blush/30 transition-all duration-300 hover:scale-[1.02] hover:shadow-glow"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-juv-rose-clay/20 to-juv-terracotta/20 rounded-full flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300">
                          {condition.icon}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-juv-deep-chestnut group-hover:text-juv-terracotta transition-colors">
                          {condition.concern}
                        </h4>
                        <p className="text-sm text-juv-deep-chestnut/80 mt-1 mb-2">
                          {condition.description}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {condition.treatments.slice(0, 2).map((treatment) => (
                            <span 
                              key={treatment}
                              className="text-xs bg-juv-soft-blush text-juv-deep-chestnut px-2 py-1 rounded-full"
                            >
                              {treatment}
                            </span>
                          ))}
                          {condition.treatments.length > 2 && (
                            <span className="text-xs text-juv-deep-chestnut/60">+{condition.treatments.length - 2}</span>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-juv-soft-blush to-juv-rose-clay/10 border-t border-juv-rose-clay/20 p-6">
                <div className="text-center">
                  <h4 className="font-semibold text-juv-deep-chestnut mb-2">Want a personalized recommendation?</h4>
                  <p className="text-sm text-juv-deep-chestnut/80 mb-4">Our Glow Quiz will match you to the perfect treatments for your unique concerns</p>
                  <Link
                    to="/quiz"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-juv-rose-clay to-juv-terracotta text-white px-6 py-3 rounded-full hover:shadow-glow transition-all duration-300 hover:scale-105"
                  >
                    <span className="font-medium">Take the Glow Quiz</span>
                    <span>✨</span>
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConditionsMegaMenu;
